import { get } from 'vuex-pathify';
import { unByKey } from 'ol/Observable';
import { createEmpty, extend } from 'ol/extent.js';
export default {
  data: () => ({
    hoveredFeature: null,
    pointerCoords: [],
  }),
  computed: {
    currentLngPanoramaLayer: get('admin/settings@PANORAMA_SETTING.value.layers@:currentLng'),
    currentLngPanoramaLayerUrlAttribute: get('admin/settings@PANORAMA_SETTING.value.url_attribute'),
    currentLng() {
      return this.$i18n.locale;
    },
    specialIdentificationLayersDict() {
      return {
        [this.currentLngPanoramaLayer]: this.openPanorama,
        ...[
          ...Object.values(this.offersCartogramLayersIds),
          ...Object.values(this.offersLayersIds[this.currentLng]),
        ].reduce((total, current) => {
          total[current] = this.toggleOffersTree;
          return total;
        }, {}),
      };
    },
    specialIdentificationLayers() {
      return Object.keys(this.specialIdentificationLayersDict).map(layerId => +layerId);
    },
  },
  methods: {
    specialLayerFilter(layer) {
      return !layer.get('isSpecial');
    },
    attachListener(eventType, callback, { type = 'on', cursor = 'crosshair' } = {}) {
      if (cursor) {
        this.map.getViewport().style.cursor = cursor;
      }
      this.listenerKey = this.map[type](eventType, callback);
    },
    attachMoveCursorHandler(cursor) {
      this.pointerMoveKey = this.map.on('pointermove', evt => {
        this.pointerCoords = evt['coordinate'];
        const hit = this.map.hasFeatureAtPixel(evt.pixel, {
          layerFilter: this.specialLayerFilter,
        });
        if (this.hoveredFeature) {
          if (Object.values(this.offersLayersIds[this.currentLng]).includes(this.hoveredFeature.layerId)) {
            this.$store.set('map/DELETE_SELECTED_FEATURES!', {
              type: 'highlitedFeatures',
              features: {
                [this.hoveredFeature.layerId]: [this.hoveredFeature.id],
              },
            });
          } else if (Object.values(this.offersCartogramLayersIds).includes(this.hoveredFeature.layerId)) {
            const hoveredMapFeature = this.getLayerBy(this.hoveredFeature.layerId, { group: 'offersCartogramLayers' })
              ?.getSource()
              .getFeatureById(this.hoveredFeature.id);
            if (hoveredMapFeature) hoveredMapFeature.set('hovered', false);
          }
          this.setHoveredFeature(null);
        }
        if (hit) {
          this.hoverFeature(evt.coordinate);
        }
        this.map.getViewport().style.cursor = hit ? cursor : '';
      });
    },
    dettachCursorMoveHandler() {
      unByKey(this.pointerMoveKey);
    },
    deactivateTool() {
      unByKey(this.listenerKey);
      this.map.getViewport().style.cursor = '';
      this.activeControl = '';
    },
    toggleIdentification(value) {
      this.activeControl = value ? 'identification' : '';
      if (value) {
        this.attachListener(
          'singleclick',
          e => {
            const { coordinate } = e;
            this.identifyCoordinatesHandler(coordinate);
          },
          {
            type: 'on',
            cursor: null,
          }
        );
        this.activeControl = 'identification';
        this.attachMoveCursorHandler('pointer');
      } else {
        this.dettachCursorMoveHandler();
        this.deactivateTool();
      }
    },
    identifyCoordinatesHandler(coordinate) {
      const features = this.identifyCoordinates(coordinate);
      if (features.length === 0) {
        return;
      } else if (features.length === 1) {
        const { layerId: lid, id: fid, properties } = features[0];
        if (this.specialIdentificationLayers.includes(lid)) {
          this.specialIdentificationLayersDict[lid](features[0], true);
        } else {
          if (properties.type === 'cluster') {
            if (properties.areFeaturesClustered) this.zoomCluster(properties.features);
          } else {
            this.pushFeatureCardRoute(lid, fid);
          }
        }
      } else {
        const identifiedFeature = features.find(feature => !this.specialIdentificationLayers.includes(feature.layerId));
        if (identifiedFeature) {
          const { layerId: lid, id: fid, properties } = identifiedFeature;
          if (properties.type === 'cluster') {
            if (properties.areFeaturesClustered) this.zoomCluster(properties.features);
          } else {
            this.pushFeatureCardRoute(lid, fid);
          }
        }
        const groupedFeatures = this.groupFeaturesByLayer(features);
        this.specialIdentificationLayers.forEach(layer => {
          const feature = groupedFeatures[layer]?.[0];
          if (feature) this.specialIdentificationLayersDict[layer](feature, !identifiedFeature);
        });
      }
    },
    identifyCoordinates(coordinate) {
      const pixel = this.map.getPixelFromCoordinate(coordinate);
      return this.getFeaturesAtPixel(pixel);
    },
    hoverFeature(coordinate) {
      const pixel = this.map.getPixelFromCoordinate(coordinate);
      const hoveredFeatures = this.getFeaturesAtPixel(pixel);
      if (!hoveredFeatures || hoveredFeatures.length === 0) return;
      const hoveredFeature = hoveredFeatures.find(feature =>
        [
          ...Object.values(this.offersCartogramLayersIds),
          ...Object.values(this.offersLayersIds[this.currentLng]),
        ].includes(feature.layerId)
      );
      if (!hoveredFeature) return;
      const { id, layerId, type } = hoveredFeature;
      this.setHoveredFeature({
        id,
        layerId,
        type,
      });
      if (Object.values(this.offersLayersIds[this.currentLng]).includes(this.hoveredFeature.layerId)) {
        this.$store.set('map/ADD_SELECTED_FEATURES!', {
          type: 'highlitedFeatures',
          features: {
            [this.hoveredFeature.layerId]: [this.hoveredFeature.id],
          },
        });
      } else if (Object.values(this.offersCartogramLayersIds).includes(this.hoveredFeature.layerId)) {
        const hoveredMapFeature = this.getLayerBy(this.hoveredFeature.layerId, { group: 'offersCartogramLayers' })
          ?.getSource()
          .getFeatureById(this.hoveredFeature.id);
        if (hoveredMapFeature) hoveredMapFeature.set('hovered', true);
      }
    },
    setHoveredFeature(feature) {
      this.hoveredFeature = feature;
    },
    getFeaturesAtPixel(pixel) {
      const features = [];
      this.map.forEachFeatureAtPixel(
        pixel,
        (feature, layer) => {
          const { id: layerId, name, type } = layer.getProperties();
          const featureId = feature.getId() || feature.get('id');
          if (this.isFeatureAlreadyIdentified(layerId, featureId, features)) {
            return;
          }
          features.push({
            id: featureId,
            type,
            layerId,
            name,
            properties: { ...feature.getProperties(), id: featureId },
          });
        },
        {
          hitTolerance: 5,
          layerFilter: this.specialLayerFilter,
        }
      );
      return features;
    },
    isFeatureAlreadyIdentified(layerId, featureId, featuresArray) {
      return featuresArray.find(feature => feature.id === featureId && feature.layerId === layerId);
    },
    pushFeatureCardRoute(lid, fid, query) {
      this.$router
        .push({
          name: 'offer',
          query: query || this.$route.query,
          params: {
            lid,
            fid,
          },
        })
        .catch(() => {});
    },
    groupFeaturesByLayer(features) {
      return features.reduce((total, current) => {
        return { ...total, [current.layerId]: [...(total[current.layerId] || []), current] };
      }, {});
    },

    openPanorama(feature) {
      const url = feature.properties?.[this.currentLngPanoramaLayerUrlAttribute];
      if (url) {
        window.open(url, '_blank');
      }
    },
    zoomCluster(clusterFeatures) {
      const extent = createEmpty();
      clusterFeatures.forEach(ft => extend(extent, ft.getGeometry().getExtent()));
      this.map.getView().fit(extent, { duration: 0, padding: [100, 100, 100, 100] });
    },
    toggleOffersTree(feature, single) {
      if (single) {
        if (this.currentTreeStatus['layer'] || this.currentTreeStatus['layer'] === 0 || feature.type === 'offer') {
          const currentTreeLayer = this.currentTree.find(
            layer => layer.id === (this.currentTreeStatus['layer'] || feature.layerId)
          );
          const dict = {
            voivodeship: 0,
            district: 1,
            commune: 2,
          };
          if (Object.keys(dict).includes(feature.type) || feature.type === 'offer') {
            let query = JSON.parse(JSON.stringify(this.$route.query));
            const level = dict[feature.type];
            Object.keys(query).forEach(key => {
              if (+key > level) delete query[key];
            });
            if (feature.type === 'district') {
              if (!this.currentTreeStatus['voivodeship'] && this.currentTreeStatus['voivodeship'] !== 0) {
                const voivodeship = currentTreeLayer.children.find(v => v.children.find(d => d.id === feature.id));
                if (voivodeship) {
                  query[0] = voivodeship.id;
                }
              }
            } else if (feature.type === 'commune') {
              if (
                (!this.currentTreeStatus['voivodeship'] && this.currentTreeStatus['voivodeship'] !== 0) ||
                (!this.currentTreeStatus['district'] && this.currentTreeStatus['district'] !== 0)
              ) {
                const voivodeship = currentTreeLayer.children.find(v =>
                  v.children.find(d => d.children.find(c => c.id === feature.id))
                );
                const district = voivodeship.children.find(d => d.children.find(c => c.id === feature.id));
                query = {
                  ...query,
                  ...(district ? { [1]: district.id } : {}),
                  ...(voivodeship ? { [0]: voivodeship.id } : {}),
                };
              }
            } else if (feature.type === 'offer') {
              if (
                (!this.currentTreeStatus['voivodeship'] && this.currentTreeStatus['voivodeship'] !== 0) ||
                (!this.currentTreeStatus['district'] && this.currentTreeStatus['district'] !== 0) ||
                (!this.currentTreeStatus['commune'] && this.currentTreeStatus['commune'] !== 0)
              ) {
                const voivodeship = currentTreeLayer.children.find(v =>
                  v.children.find(d => d.children.find(c => c.children.find(o => o.id === feature.id)))
                );
                const district = voivodeship.children.find(d =>
                  d.children.find(c => c.children.find(o => o.id === feature.id))
                );
                const commune = district.children.find(c => c.children.find(o => o.id === feature.id));
                query = {
                  ...query,
                  ...(commune ? { [2]: commune.id } : {}),
                  ...(district ? { [1]: district.id } : {}),
                  ...(voivodeship ? { [0]: voivodeship.id } : {}),
                };
              }
            }
            if (feature.type === 'offer') this.pushFeatureCardRoute(feature.layerId, feature.id, query);
            else this.$router.push({ ...this.$route, query: { ...query, [level]: feature.id } });
          }
        } else {
          this.fitViewToMergedClusterElement(feature);
        }
      }
    },
  },
  watch: {
    currentLng(nV, oV) {
      if (this.currentTreeStatusLayerId || this.currentTreeStatusLayerId === 0) {
        const oType = Object.keys(this.offersLayersIds[oV]).find(
          key => this.offersLayersIds[oV][key] === this.currentTreeStatusLayerId
        );
        const nTypeLayer = this.offersLayersIds[nV][oType];
        if (nTypeLayer) this.$router.push({ ...this.$route, params: { ...this.$route.params, lid: nTypeLayer } });
      }
    },
  },
  mounted() {
    this.$root.$off('toggleIdentification');
    this.$root.$on('toggleIdentification', this.toggleIdentification);
  },
};
